/**
 * Prospect tool surface (/tools) — Layered Access Phase 2.
 *
 * A role-gated EKSELENS360 workspace for trial / campaign users. They can run
 * ONE full assessment and download watermarked trial copies of the board pack
 * and board paper — without entering the staff admin dashboard.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Clock, Lock, Download, FileText, Loader2, CheckCircle2,
  Sparkles, ShieldCheck,
} from "lucide-react";
import Layout from "../components/Layout";
import api, { API_BASE } from "../lib/api";
import SessionTimeoutGuard from "../components/SessionTimeoutGuard";

const SECTORS = [
  "Financial Services", "Public Sector", "Telecom", "Manufacturing",
  "Retail & Consumer", "Energy & Utilities", "Healthcare", "Technology",
  "Professional Services", "Other",
];

const downloadExport = async (runId, fmt, filename) => {
  try {
    const res = await fetch(`${API_BASE}/tools/runs/${runId}/export/${fmt}`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed", { description: "Make sure your assessment is complete." });
  }
};

const Eyebrow = ({ children }) => (
  <div className="ek-eyebrow text-[#A179FA] mb-3">{children}</div>
);

function TrialBanner({ me }) {
  if (!me?.trial) return null;
  const { days_remaining, expired } = me.trial;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 text-[13px] border ${
        expired ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-[#F4EEFD] border-[#E0CFFB] text-[#4B2E83]"
      }`}
      data-testid="tools-trial-banner"
    >
      <Clock size={16} />
      {expired ? (
        <span>Your trial window has ended. <a href="/contact" className="underline">Book a discovery conversation</a> to continue.</span>
      ) : (
        <span>
          <strong>{days_remaining} day{days_remaining === 1 ? "" : "s"}</strong> left in your evaluation window.
          Outputs are watermarked trial copies.
        </span>
      )}
    </div>
  );
}

// ---------- Denied / not eligible ----------
function Denied() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-black text-[#A179FA] mb-6"><Lock size={22} /></div>
        <h1 className="ek-heading text-3xl md:text-4xl" data-testid="tools-denied-title">Your access has ended</h1>
        <p className="text-zinc-600 mt-4 text-[15px] leading-relaxed">
          The EKSELENS360 evaluation workspace is available to active trial and campaign accounts.
          Your window may have expired, or this account isn&apos;t set up for tool access yet.
        </p>
        <a href="/contact" className="ek-btn ek-btn-primary mt-8 inline-flex" data-testid="tools-denied-cta">
          Book a discovery conversation <ArrowRight size={14} />
        </a>
      </div>
    </Layout>
  );
}

// ---------- Setup: pick framework + client ----------
function Setup({ me, frameworks, onStarted }) {
  const [frameworkId, setFrameworkId] = useState(frameworks[0]?.id || "");
  const [clientName, setClientName] = useState("");
  const [sector, setSector] = useState(SECTORS[0]);
  const [busy, setBusy] = useState(false);

  const start = async (e) => {
    e.preventDefault();
    if (!frameworkId || !clientName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tools/runs", {
        framework_id: frameworkId,
        weighting_profile_id: "default",
        client: { name: clientName.trim(), sector },
      });
      onStarted(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message || "Could not start" : d || "Could not start the assessment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <Eyebrow>EKSELENS360 · Evaluation</Eyebrow>
      <h1 className="ek-heading text-3xl md:text-4xl leading-[1.08]">Run your maturity assessment</h1>
      <p className="text-zinc-600 mt-4 text-[15px] leading-relaxed">
        Pick the capability area, answer the diagnostic, and generate a board-grade improvement plan.
        Your trial includes <strong>one full assessment</strong>.
      </p>
      <div className="mt-6"><TrialBanner me={me} /></div>

      <form onSubmit={start} className="mt-8 space-y-5 bg-white border border-zinc-200 p-6" data-testid="tools-setup-form">
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Capability area</span>
          <select
            value={frameworkId}
            onChange={(e) => setFrameworkId(e.target.value)}
            className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] bg-white"
            data-testid="tools-framework-select"
          >
            {frameworks.map((f) => (
              <option key={f.id} value={f.id}>{f.sub_brand} — {f.name}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Organisation name</span>
          <input
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required maxLength={200} placeholder="e.g. Northwind Group"
            className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px]"
            data-testid="tools-client-name-input"
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Sector</span>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] bg-white"
            data-testid="tools-sector-select"
          >
            {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button
          type="submit"
          disabled={busy || !clientName.trim()}
          className="ek-btn ek-btn-primary w-full justify-center disabled:opacity-50"
          data-testid="tools-start-btn"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {busy ? "Starting…" : "Start assessment"}
        </button>
      </form>
    </div>
  );
}

// ---------- Answer ----------
function Answer({ me, framework, run, onComplete }) {
  const [answers, setAnswers] = useState(run.answers || {});
  const [scores, setScores] = useState(run.scores || null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const saveTimer = useRef(null);

  const totalIndicators = useMemo(
    () => framework.dimensions.reduce((n, d) => n + d.indicators.length, 0),
    [framework]
  );
  const answeredCount = Object.keys(answers).length;
  const pct = totalIndicators ? Math.round((answeredCount / totalIndicators) * 100) : 0;

  const setAnswer = (indId, score) => {
    setAnswers((prev) => ({ ...prev, [indId]: { score } }));
    // debounced autosave of just the changed answer (backend merges)
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.patch(`/tools/runs/${run.id}`, { answers: { [indId]: { score } } });
        setScores(data.scores);
      } catch {
        toast.error("Couldn't save that answer");
      } finally {
        setSaving(false);
      }
    }, 350);
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/tools/runs/${run.id}/complete`);
      onComplete(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Answer every indicator first");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <Eyebrow>{framework.sub_brand} · {run.client?.name}</Eyebrow>
      <h1 className="ek-heading text-2xl md:text-3xl">{framework.name}</h1>
      <div className="mt-4"><TrialBanner me={me} /></div>

      {/* progress */}
      <div className="sticky top-[132px] z-20 bg-white/95 backdrop-blur py-3 mt-6 border-b border-zinc-200">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-zinc-600" data-testid="tools-progress-label">
            {answeredCount}/{totalIndicators} answered · {pct}%
          </span>
          <span className="text-zinc-400 flex items-center gap-1">
            {saving ? <><Loader2 size={12} className="animate-spin" /> saving…</> : "auto-saved"}
          </span>
        </div>
        <div className="h-1.5 bg-zinc-200 mt-2 overflow-hidden">
          <div className="h-full bg-[#7737F6] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mt-8 space-y-10">
        {framework.dimensions.map((d) => (
          <section key={d.id} data-testid={`tools-dimension-${d.id}`}>
            <h2 className="ek-heading text-lg text-black">{d.name}</h2>
            <p className="text-[12px] text-zinc-400 uppercase tracking-[0.14em] mt-1">{d.short}</p>
            <div className="mt-4 space-y-5">
              {d.indicators.map((ind) => (
                <div key={ind.id} className="border border-zinc-200 p-4" data-testid={`tools-indicator-${ind.id}`}>
                  <p className="text-[14px] text-zinc-800 leading-relaxed">{ind.prompt}</p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-5 gap-2">
                    {ind.options.map((opt) => {
                      const active = answers[ind.id]?.score === opt.score;
                      return (
                        <button
                          key={opt.score}
                          type="button"
                          onClick={() => setAnswer(ind.id, opt.score)}
                          className={`text-left px-3 py-2 border text-[12.5px] transition-colors ${
                            active
                              ? "bg-black text-white border-black"
                              : "bg-white text-zinc-600 border-zinc-300 hover:border-[#7737F6]"
                          }`}
                          data-testid={`tools-opt-${ind.id}-${opt.score}`}
                        >
                          <span className={`font-semibold ${active ? "text-[#A179FA]" : "text-[#7737F6]"}`}>{opt.score}</span>
                          <span className="block mt-0.5 leading-snug">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12 flex items-center justify-end gap-4 border-t border-zinc-200 pt-6">
        {pct < 100 && <span className="text-[13px] text-zinc-500">Answer all indicators to finish.</span>}
        <button
          onClick={complete}
          disabled={pct < 100 || completing}
          className="ek-btn ek-btn-primary disabled:opacity-50"
          data-testid="tools-complete-btn"
        >
          {completing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          {completing ? "Finishing…" : "Complete assessment"}
        </button>
      </div>
    </div>
  );
}

// ---------- Result ----------
function Result({ me, framework, run }) {
  const scores = run.scores || {};
  const [plan, setPlan] = useState(null);
  const [planStatus, setPlanStatus] = useState(null); // null|generating|ready|failed
  const pollRef = useRef(null);

  useEffect(() => {
    // check for an existing plan once
    api.get(`/tools/runs/${run.id}/plan`).then(({ data }) => {
      setPlan(data.plan);
      setPlanStatus(data.plan?.status || "ready");
    }).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [run.id]);

  const generatePlan = async () => {
    setPlanStatus("generating");
    try {
      await api.post(`/tools/runs/${run.id}/plan`, {});
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/tools/runs/${run.id}/plan`);
          setPlan(data.plan);
          if (data.plan?.status === "ready" || data.plan?.status === "failed") {
            setPlanStatus(data.plan.status);
            clearInterval(pollRef.current);
            if (data.plan.status === "ready") toast.success("Improvement plan ready");
            if (data.plan.status === "failed") toast.error("Plan generation failed — please retry");
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      setPlanStatus("failed");
      toast.error(err?.response?.data?.detail || "Could not start plan generation");
    }
  };

  const safe = (run.client?.name || "assessment").replace(/[^a-z0-9]+/gi, "-");

  return (
    <div className="max-w-3xl mx-auto px-6 py-14">
      <Eyebrow>{framework.sub_brand} · {run.client?.name}</Eyebrow>
      <h1 className="ek-heading text-3xl md:text-4xl">Your maturity result</h1>
      <div className="mt-4"><TrialBanner me={me} /></div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-black text-white p-5" data-testid="tools-overall-score">
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#A179FA]">Overall maturity</div>
          <div className="text-4xl font-semibold mt-2">{scores.overall_weighted ?? "—"}<span className="text-zinc-500 text-xl">/5</span></div>
          <div className="text-[13px] text-zinc-400 mt-1">{scores.overall_level || ""}</div>
        </div>
        {(scores.by_dimension || []).slice(0, 2).map((d) => (
          <div key={d.dimension_id} className="bg-[#F2F2F3] p-5">
            <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500">{d.level_label}</div>
            <div className="text-2xl font-semibold mt-2 text-black">{d.weighted_score}</div>
            <div className="text-[12px] text-zinc-500 mt-1">{d.dimension_id}</div>
          </div>
        ))}
      </div>

      {/* Plan */}
      <div className="mt-10 bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 text-[#7737F6]"><Sparkles size={16} /><span className="ek-eyebrow text-[#7737F6]">Improvement plan</span></div>
        <p className="text-zinc-600 text-[14px] mt-2 leading-relaxed">
          Generate a board-grade improvement plan (powered by Claude Opus). It produces the board-decision
          section needed for your board paper. This takes ~90 seconds.
        </p>
        {planStatus !== "ready" && (
          <button
            onClick={generatePlan}
            disabled={planStatus === "generating"}
            className="ek-btn ek-btn-primary mt-4 disabled:opacity-50"
            data-testid="tools-generate-plan-btn"
          >
            {planStatus === "generating" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {planStatus === "generating" ? "Generating… (~90s)" : "Generate improvement plan"}
          </button>
        )}
        {planStatus === "ready" && (
          <div className="mt-3 flex items-center gap-2 text-emerald-700 text-[13px]" data-testid="tools-plan-ready">
            <CheckCircle2 size={15} /> Plan ready.
          </div>
        )}
      </div>

      {/* Downloads */}
      <div className="mt-8">
        <div className="ek-eyebrow text-[#A179FA] mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Trial deliverables (watermarked)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => downloadExport(run.id, "pdf", `Ekselens-${safe}-board-pack.pdf`)}
            className="flex items-center gap-3 border border-zinc-300 px-4 py-3 text-left hover:border-[#7737F6] transition-colors"
            data-testid="tools-download-boardpack"
          >
            <Download size={18} className="text-[#7737F6]" />
            <span><span className="block text-[14px] font-medium text-black">Board pack (PDF)</span>
              <span className="block text-[12px] text-zinc-500">Full diagnostic & roadmap</span></span>
          </button>
          <button
            onClick={() => downloadExport(run.id, "boardpaper", `Ekselens-${safe}-board-paper.pdf`)}
            disabled={planStatus !== "ready"}
            className="flex items-center gap-3 border border-zinc-300 px-4 py-3 text-left hover:border-[#7737F6] transition-colors disabled:opacity-40 disabled:hover:border-zinc-300"
            data-testid="tools-download-boardpaper"
          >
            <FileText size={18} className="text-[#7737F6]" />
            <span><span className="block text-[14px] font-medium text-black">Board Decision Paper (PDF)</span>
              <span className="block text-[12px] text-zinc-500">{planStatus === "ready" ? "1–2 page formal submission" : "Generate the plan first"}</span></span>
          </button>
        </div>
      </div>

      <div className="mt-12 border-t border-zinc-200 pt-6 text-center">
        <p className="text-zinc-600 text-[14px]">Want the full, co-branded deliverables and our team alongside you?</p>
        <a href="/contact" className="ek-btn ek-btn-primary mt-4 inline-flex" data-testid="tools-result-cta">
          Book a discovery conversation <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

export default function ProspectTools() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading|denied|setup|answer|result
  const [me, setMe] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [framework, setFramework] = useState(null);
  const [run, setRun] = useState(null);

  const loadFrameworkAndRun = async (runData) => {
    const { data: fw } = await api.get(`/tools/frameworks/${runData.framework_id}`);
    setFramework(fw);
    setRun(runData);
    setPhase(runData.status === "completed" ? "result" : "answer");
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tools/me");
        setMe(data);
        if (data.active_run) {
          const { data: full } = await api.get(`/tools/runs/${data.active_run.id}`);
          await loadFrameworkAndRun(full);
        } else {
          const { data: fl } = await api.get("/tools/frameworks");
          setFrameworks(fl.items || []);
          setPhase("setup");
        }
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401) { navigate("/signin"); return; }
        setPhase("denied");
      }
    })();
  }, [navigate]);

  if (phase === "loading") {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 size={28} className="animate-spin text-[#7737F6]" />
        </div>
      </Layout>
    );
  }
  if (phase === "denied") return <Denied />;

  return (
    <Layout>
      <SessionTimeoutGuard redirectTo="/signin" />
      <div data-testid="prospect-tools-page">
        {phase === "setup" && (
          <Setup me={me} frameworks={frameworks} onStarted={(r) => loadFrameworkAndRun(r)} />
        )}
        {phase === "answer" && framework && run && (
          <Answer me={me} framework={framework} run={run} onComplete={(r) => { setRun(r); setPhase("result"); }} />
        )}
        {phase === "result" && framework && run && (
          <Result me={me} framework={framework} run={run} />
        )}
      </div>
    </Layout>
  );
}
